import React, { useEffect, useState } from 'react';
import 'react-bootstrap-typeahead/css/Typeahead.css';
import { getAllEmployees, getAllSupervisors, getAllUsers } from 'shared/services/userApi';
import { getLocationsWithDetail } from 'shared/services/locationApi';
import { IUserSearchResult, LocationDetail } from 'shared/models/api/models';
import { toISOLocalDate, isoDateStringToBrowserSafeString } from 'shared/models/mapping/mapping';
import { NearMeOutlined, SupervisorAccountOutlined, ExpandMore, ExpandLess } from '@mui/icons-material';
import Form from 'react-bootstrap/Form'

import { SubmitActionModel } from 'shared/models/api/models';
import { List, IListItemProps } from 'components/ui/Lists';
import { LabelledCheckbox } from 'components/ui/LabelledCheckbox';
import { FatigueAssessmentActionState, fatigueAssessmentActionState } from './FatigueAssessmentActionState'
import {
    FatigueAssessmentFormState,
    fatigueAssessmentFormState as fs,
} from './FatigueAssessmentFormState'

import {
    FormFeedback,
    Input,
    Label,
    UncontrolledDropdown,
    DropdownToggle,
    DropdownMenu,
    DropdownItem
} from 'reactstrap';

interface OptionValueLabel {
    value: number;
    label: string;
}

interface AnswerProps {
    onAnswerUpdate: (value: any) => any;
    invalid?: boolean;
    optional?: boolean;
}

const getAlertOptions = () => {
    let o = [
        { value: 0, label: "Fully alert" },
        { value: 1, label: "Very lively" },
        { value: 2, label: "Okay" },
        { value: 3, label: "A little tired" },
        { value: 4, label: "Moderately tired" },
        { value: 5, label: "Extremely tired" },
        { value: 6, label: "Completely exhausted" }
    ] as OptionValueLabel[];
    return o;
}


export const alertOptions = getAlertOptions();

const getYesNoOptions = () => {
    let o = [
        { value: 1, label: "Yes" },
        { value: 0, label: "No" }
    ] as OptionValueLabel[];
    return o;
}
export const yesNoOptions = getYesNoOptions();

const getSexOptions = () => {
    let o = [
        { value: 0, label: "Male" },
        { value: 1, label: "Female" },
        { value: 2, label: "Prefer not to disclose" }
    ] as OptionValueLabel[];
    return o;
}
export const sexOptions = getSexOptions();

const getRiskOptions = () => {
    let o = [
        { value: 0, label: "N/A" },
        { value: 1, label: "Low" },
        { value: 2, label: "Medium" },
        { value: 3, label: "High" }
    ] as OptionValueLabel[];
    return o;
}
export const riskOptions = getRiskOptions();

const getActionOptions = () => {
    let o = {
        noFurtherAction: "No further action required",
        noActionTakenPlannedLeave: "Planned Leave",
        noActionTakenUnplannedLeave: "Unplanned Leave",
        actionTaken: "Action has been taken",
        actionTakenRevisit: "Revisit Fatigue Assessment",
        actionTakenBuddyUp: "Buddy Up",
        actionTakenRemoveFromSafetyCritical: "Remove from Safety Critical",
        actionTakenRemainLocal: "Remain Local",
        actionTakenAltDuties: "Alternate Duties - Office Bound",
        sentHome: "Sent Home"
    }
    return o;
}
export const actionOptions = getActionOptions();


const getOnbehalfOptions = () => {
    let o = [
        { value: 0, label: "DSS Microsleep" },
        { value: 1, label: "Person unable to log on" },
        { value: 2, label: "Previous medium high risk recorded" },
        { value: 3, label: "Remote Sign On" },
        { value: -1, label: "Other" }
    ] as OptionValueLabel[];
    return o;
}
export const onbehalfOptions = getOnbehalfOptions();

interface SelectAnswerProps extends AnswerProps {
    options: OptionValueLabel[];
    selectedAnswer?: OptionValueLabel;
}

export const DropdownSelect = (props: SelectAnswerProps) => {
    const [selectedAnswer, setSelectedAnswer] = useState(props.selectedAnswer);

    const answerSelected = (choice: OptionValueLabel) => {
        setSelectedAnswer(choice);
        props.onAnswerUpdate(choice);
    }

    const isOptional = () => props.optional === true; //default to false if not provided

    useEffect(() => {
        // Fixes a bug on safari where dropdown unpopulates
        if (props.selectedAnswer) {
            setSelectedAnswer(props.selectedAnswer);
        }
    }, [props.selectedAnswer]);

    return (
        <div className={`main-form-selector`}>
            <UncontrolledDropdown>
                <DropdownToggle
                    className={`form-control${ props.invalid && !isOptional() ? ' is-invalid' : '' }${ !selectedAnswer ? ' is-faded' : '' }`}
                    tag="div">
                    <div className="fa-select-options">
                        {selectedAnswer ? selectedAnswer.label : <span className="fa-select-default">Select</span>}
                    </div>
                    <div className="fa-select-button">
                        <ExpandMore className="expanded-show" />
                        <ExpandLess className="expanded-hide" />
                    </div>
                </DropdownToggle>
                <DropdownMenu>
                    {props.options.map((choice) => (
                        <DropdownItem
                            key={choice.value}
                            className={`form-check-inline dropdown-item ${ choice.value }`}
                            onClick={() => answerSelected(choice)}>
                            <Label className={`form-check-label`}>{choice.label}</Label>
                        </DropdownItem>
                    ))}
                </DropdownMenu>
            </UncontrolledDropdown>
            <FormFeedback className={`${ props.invalid && !isOptional() ? "d-block" : "" }`}>Required</FormFeedback>
        </div>
    );
}

interface HoursChoiceProps extends AnswerProps{
    minTime: number;
    maxTime: number;
}

export const HoursChoices = (props: HoursChoiceProps) => {
    const minTime = props.minTime;
    const minTimeMessage = `${props.minTime} hours or less`;
    const maxTime = props.maxTime;
    const maxTimeMessage = `${props.maxTime} hours or more`;
    const incrementTime = 1;

    const options = () => {
        let timeOptions = [] as number[];
        for (let time = minTime; time <= maxTime; time += incrementTime) {
            timeOptions.push(time);
        }

        let mappedOptions = timeOptions.map(option => ({ value: option, label: displayTime(option) }));        
        return mappedOptions;
    }

    const displayTime = (time: number) => {
        if (time == minTime) {
            return minTimeMessage;
        }
        if (time == maxTime) {
            return maxTimeMessage;
        }
        const hours = Math.floor(time);
        const minutes = (time * 60) % 60;
        return `${ hours }:${ minutes.toString().padStart(2, '0') }`;
    }

    return (
        <DropdownSelect {...props} options={options()} />
    );
}


export const AlertChoices = (props: AnswerProps) => {
    return (
        <>
            {alertOptions.map((option) => (
                <div key={`alert_${ option.value }`} className="answer-select row">
                    <div className="col-1">
                        <Form.Check required type="radio" id={`alert_${ option.value }`} name="alertLevel" value={option.value} onChange={props.onAnswerUpdate} />
                    </div>
                    <div className="col pl-1">
                        <label htmlFor={`alert_${ option.value }`}>{option.label}</label><br />
                    </div>
                </div>
            ))}
            <FormFeedback className={`${ props.invalid ? "d-block" : "" }`}>Required</FormFeedback>
        </>
    );
}


export const AlcoholicDrinksChoices = (props: AnswerProps) => {
    const minDrinks = 0;
    const maxDrinks = 10;
    const maxDrinksMessage = "10 or more";

    const options = () => {
        let drinksOptions = [] as number[];
        for (let drinks = minDrinks; drinks <= maxDrinks; drinks += 1) {
            drinksOptions.push(drinks);
        }

        let mappedOptions = drinksOptions.map(option => ({ value: option, label: displayDrinksChoices(option) }));
        return mappedOptions;
    }

    const displayDrinksChoices = (drinks: number) => {
        if (drinks == maxDrinks) {
            return maxDrinksMessage;
        }
        return `${ drinks }`;
    }

    return (
        <DropdownSelect {...props} options={options()} />
    );
}



export const SexChoices = (props: AnswerProps) => {
    return (
        <DropdownSelect {...props} options={sexOptions} />
    );
}


export const OnbehalfChoices = (props: AnswerProps) => {
    return (
        <DropdownSelect {...props} options={onbehalfOptions} />
    );
}

interface IYesNoProps extends AnswerProps {
    name: string;
}

export const YesNoChoice = (props: IYesNoProps) => {
    return (
        <>
            {yesNoOptions.map((option) => (
                <div key={`${ props.name }_${ option.value }`} className="answer-select row pt-1 pb-1">
                    <div className="col-1">
                        <Form.Check required type="radio" id={`${ props.name }_${ option.value }`} name={props.name} value={option.value} onChange={props.onAnswerUpdate} />
                    </div>
                    <div className="col pl-1">
                        <label htmlFor={`${ props.name }_${ option.value }`}>{option.label}</label><br />
                    </div>
                </div>
            ))}
            <FormFeedback className={`${ props.invalid ? "d-block" : "" }`}>Required</FormFeedback>
        </>
    );
}


export const YesNoCheck = (props: IYesNoProps) => {
    return (
        <>

            <div key={`${ props.name }`} className="answer-select row pt-1 pb-1">
                <div className="col-1">
                    <Form.Check required type="checkbox" id={`${ props.name }`} name={props.name} onChange={props.onAnswerUpdate} />
                </div>

            </div>
            <FormFeedback className={`${ props.invalid ? "d-block" : "" }`}>Required</FormFeedback>
        </>
    );
}


export const UserListItem = (props: IListItemProps<IUserSearchResult>) =>
    <div className="list-item user-list-item" onClick={() => props.toggle(props.item)}>
        <LabelledCheckbox checkedValue={props.selected === true}><span className="name">{props.item.fullname}</span></LabelledCheckbox>
        <div>
            <span className="location"><NearMeOutlined /> {props.item.city}</span>
            <span className="supervisor">
                {props.item.supervisor?.length > 0 &&
                    <span><SupervisorAccountOutlined />{props.item.supervisor}</span>
                }
            </span>
        </div>
    </div>;


interface UserChoiceProps extends AnswerProps {
    defaultUser?: IUserSearchResult,
    multichoice?: boolean,
    supervisorFilter?: boolean,
    clearOnSelect?: boolean,
    subscribeActionField?: string,
    assign?: boolean
}


export const UserChoice = (props: UserChoiceProps) => {
    const [users, setUsers] = useState((props.defaultUser ? [props.defaultUser] : []) as IUserSearchResult[]);
    const [selectedUsers, setSelectedUsers] = useState(props.defaultUser ? [props.defaultUser] : [] as IUserSearchResult[]);
    const [summaryText, setSummaryText] = useState("");
    const [filterText, setFilterText] = useState("");
    const [isExpanded, setIsExpanded] = useState(false);
    const [action, setAction] = useState({} as SubmitActionModel);

    const selectUser = (user: IUserSearchResult) => {
        if (props.multichoice) {
            if (selectedUsers.includes(user)) {
                setSelectedUsers(selectedUsers.filter(u => u !== user));
            }
            else {
                setSelectedUsers(selectedUsers.concat([user]));
            }
        }
        else {
            setSelectedUsers([user]);
            setIsExpanded(false);
        }

        props.onAnswerUpdate(user);
    };

    const filterUsersAsync = async (searchTerm: string, users?: IUserSearchResult[]): Promise<IUserSearchResult[]> => {
        const lcName = searchTerm.toLowerCase();

        const result = await (users ?? []).filter(u => {
            return (u.fullname.toLowerCase().indexOf(lcName) > -1
                || (u.email && u.email.toLowerCase().indexOf(lcName) > -1));
        });

        const usersText = result.length === 1 ? "user" : "users";

        setSummaryText(`${result.length} ${usersText} found for search "${searchTerm}".`);

        return result;
    };

    const filterSelectedUsers = (userIds: number[]) => {
        let users = selectedUsers.filter(u => u.id !== null && userIds.includes(u.id));
        setSelectedUsers(users);
    }

    useEffect(() => {
        if (typeof (props.subscribeActionField) === 'string') {
            let key = props.subscribeActionField as keyof SubmitActionModel;
            let userIds = action[key] as number[];
            filterSelectedUsers(userIds);
        }
    }, [action]);

    useEffect(() => {
        if (!props.subscribeActionField) {
            return;
        }
        const sub = fatigueAssessmentActionState.subscribe((s: FatigueAssessmentActionState) => {
            setAction(s.action);
        });
        return () => { sub.unsubscribe(); };
    }, []);

    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            if (props.supervisorFilter !== true) {
                if (props.assign) {
                    var users = await getAllUsers();
                } else {
                    var users = await getAllEmployees();
                }                
            }
            else {
                var users = await getAllSupervisors();
            }

            if (mounted) {
                setUsers(users.results);
            }
        };
        mount();
        return () => { mounted = false };
    }, []);

    return (
        <div className="fa-user-select">
            <List<IUserSearchResult>
                summary={summaryText}
                dataSource={users}
                searchDelayMs={1}
                displayLoadingData={true}
                refreshFilterOnDataSourceChange={true}
                listItemType={UserListItem}
                filterText={filterText}
                setFilterText={setFilterText}
                expanded={isExpanded}
                setExpanded={setIsExpanded}
                buildKey={(item: IUserSearchResult) => { return item.id; }}
                filterAsync={filterUsersAsync}
                selected={selectedUsers}
                toggleSelected={selectUser}
                invalid={props.invalid}
                noResultsMessage="No users found"
                filterTextPlaceholder="Enter first name, last name or email address"
            />
            {!isExpanded && selectedUsers.length === 1 && selectedUsers[0]?.id && !props.multichoice &&
                <div className="fa-selected-user">{selectedUsers[0].fullname}</div>
            }
            <FormFeedback className={`${ props.invalid ? "d-block" : "" }`}>Required</FormFeedback>

        </div>
    );
}


interface LocationChoiceProps extends AnswerProps {
    defaultLocationId?: number;
}

export const OtherLocation = { value: -2, label: "Other" };

export const LocationChoice = (props: LocationChoiceProps) => {
    const [options, setOptions] = useState([] as OptionValueLabel[]);
    const [selectedAnswer, setSelectedAnswer] = useState(undefined as undefined | OptionValueLabel);


    useEffect(() => {
        let mounted = true;
        const mount = async () => {
            const results = await getLocationsWithDetail();
            if (mounted) {
                let locations = results.filter(location => (location.siteMobileNumber ?? "") !== "").map(location => ({ value: location.id, label: `${ location.name } (${ location.siteMobileNumber })` }));
                locations.push(OtherLocation);
                setOptions(locations);
                if (props.defaultLocationId !== undefined) {
                    props.onAnswerUpdate(props.defaultLocationId);
                    let defaultLocationOption = locations.find(l => l.value === props.defaultLocationId);
                    if (defaultLocationOption) {
                        setSelectedAnswer(defaultLocationOption);
                    }
                }
            }
        };


        mount();
        return () => { mounted = false };
    }, []);

    return (
        <DropdownSelect {...props} options={options} selectedAnswer={selectedAnswer} />
    );
}


interface DateChoiceProps extends AnswerProps {
    date?: Date
}

export const DateChoice = (props: DateChoiceProps) => {
    const dayRange = 1;
    const currentDate = new Date();

    function addDays(date: Date, days: number) {
        let result = new Date(date);
        result.setDate(result.getDate() + days);
        return result;
    }

    const minDate = addDays(currentDate, dayRange * -1);
    const maxDate = addDays(currentDate, dayRange);

    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        props.onAnswerUpdate(e);
    }

    useEffect(() => {
        props.onAnswerUpdate(toISOLocalDate(props.date || currentDate));
    }, [])

    return (
        <div className="main-form-selector">
            <Input
                type="date"
                id="expiryDate"
                min={toISOLocalDate(minDate)}
                max={toISOLocalDate(maxDate)}
                defaultValue={toISOLocalDate(props.date || currentDate)}
                onChange={onChange}
            />
        </div>
    );
}


interface TimeChoiceProps extends AnswerProps {
    time?: Date
}

export const TimeChoice = (props: TimeChoiceProps) => {
    const currentDate = toISOLocalDate(new Date());

    function addMinutes(date: Date, minutes: number) {
        let result = new Date(date);
        result.setTime(result.getTime() + (minutes * 60000));
        return result;
    }
    const defaultTime = () => {
        let time = new Date('2001-01-01 00:00');
        if (typeof (props.time) === 'string') {
            time = new Date(isoDateStringToBrowserSafeString(props.time));
        }
        else {
            time = new Date();
            time = addMinutes(time, 60 - time.getMinutes());

        }
        return time;
    }

    const [selectedHour, setSelectedHour] = useState(defaultTime().getHours());
    const [selectedMinute, setSelectedMinute] = useState(defaultTime().getMinutes());


    const roundDateUpToNearestHalfHour = (dateInput: Date | string | undefined) => {
        let date = new Date(dateInput ?? `${ currentDate } ${ defaultTime() || '00:00' }`);
        if (date.getMinutes() % 30 !== 0) {
            date = addMinutes(date, 30 - date.getMinutes() % 30);
        }
        return date;
    }

    const timeFormat = (hour: number, minute: number) => {

        return `${ hour.toString().padStart(2, '0') }:${ minute.toString().padStart(2, '0') }`;
    }

    const roundSelectedTimeUpToNearestHalfHour = () => {
        let date = new Date(isoDateStringToBrowserSafeString(`${ currentDate } ${ timeFormat(selectedHour, selectedMinute) }`));
        let ft = roundDateUpToNearestHalfHour(date);
        if (date.getMinutes() % 30 !== 0) {
            setSelectedHour(ft.getHours())
            setSelectedMinute(ft.getMinutes())
        }
        props.onAnswerUpdate(`${ timeFormat(ft.getHours(), ft.getMinutes()) }`);
    }

    const onHourChange = (option: OptionValueLabel) => {
        var hour = Number(option.value);
        var minute = selectedMinute;
        setSelectedHour(hour);

        props.onAnswerUpdate(`${ timeFormat(hour, minute) }`);
    }

    const onMinuteChange = (option: OptionValueLabel) => {
        var hour = selectedHour;
        var minute = Number(option.value);
        setSelectedMinute(minute);
        props.onAnswerUpdate(`${ timeFormat(hour, minute) }`);
    }

    useEffect(() => {
        roundSelectedTimeUpToNearestHalfHour();
    }, [props.time])

    const hourOptions = () => {
        var options = [] as OptionValueLabel[];
        for (let i = 0; i < 24; i++) {
            options.push({ value: i, label: i.toString().padStart(2, '0') });
        }        
        return options;
    }

    const minuteOptions = () => {
        var options = [
            { value: 0, label: '00' },
            { value: 30, label: '30' }
        ] as OptionValueLabel[];        
        return options;
    }

    return (
        <div className="main-form-selector">
            <div className="fa-small-picker">
                <DropdownSelect
                    onAnswerUpdate={onHourChange}
                    options={hourOptions()}
                    selectedAnswer={hourOptions().find(o => o.value == selectedHour)}
                />
            </div>
            <div className="fa-small-picker">
                <DropdownSelect
                    onAnswerUpdate={onMinuteChange}
                    options={minuteOptions()}
                    selectedAnswer={minuteOptions().find(o => o.value == selectedMinute)}
                />
            </div>
        </div>
    );
}


interface TextInputProps extends AnswerProps {
    placeholder?: string;
}

export const SingleLineTextInput = (props: TextInputProps) => {
    const [text, setText] = useState('');
    const maxLength = 100;

    const answerUpdate = (event: any) => {
        let value = (event?.target?.value || '').substring(0, maxLength) as string;
        props.onAnswerUpdate(value);
        setText(value);
    }

    return (
        <>
            <Input
                type="text"
                value={text}
                placeholder={props.placeholder || `Enter text here (max. ${ maxLength } characters)`}
                maxLength={maxLength}
                onChange={answerUpdate}
                required={true}
                invalid={props.invalid}
            />
            <FormFeedback>Required</FormFeedback>
        </>
    )
}

export const MultilineTextInput = (props: TextInputProps) => {
    const [text, setText] = useState('');
    const maxLength = 500;

    const answerUpdate = (event: any) => {
        let value = (event?.target?.value || '').substring(0, maxLength) as string;
        props.onAnswerUpdate(value);
        setText(value);
    }

    return (
        <>
            <Input
                type="textarea"
                value={text}
                className="multiline-input"
                placeholder={props.placeholder || `Enter text here (max. ${ maxLength } characters)`}
                maxLength={maxLength}
                rows={5}
                onChange={answerUpdate}
                required={true}
                invalid={props.invalid}
            />
            <FormFeedback>Required</FormFeedback>
        </>
    )
}


interface RadioInputProps extends AnswerProps {
    name: string;
    id: string;
    label?: string;
}

export const RadioInput = (props: RadioInputProps) => {
    return (
        <div className="answer-select row pt-1 pb-1">
            <div className="col-1">
                <Form.Check
                    type="radio"
                    name={props.name}
                    id={props.id}
                    onChange={(e) => { props.onAnswerUpdate(props.id)(e.target.checked) }} />
            </div>
            <div className="col pl-1">
                <Label htmlFor={props.id}>{props.label}</Label>
            </div>
        </div>
    )
}

interface CheckBoxInputProps extends AnswerProps {
    id: string;
    label?: string;
    checked?: boolean;
}

export const CheckBoxInput = (props: CheckBoxInputProps) => {
    const [checked, setChecked] = useState(false);

    return (
        <div className="answer-select row pt-1 pb-1">
            <div className="col-1">
                <Form.Check
                    type="checkbox"
                    id={props.id}
                    checked={props.checked !== undefined ? props.checked : checked}
                    onChange={(e) => {
                        setChecked(e.target.checked);
                        props.onAnswerUpdate(props.id)(e.target.checked);
                    }} />
            </div>
            <div className="col pl-1">
                <Label htmlFor={props.id}>{props.label}</Label>
            </div>
        </div>
    )
}
